import axios, { CancelToken, CancelTokenSource } from 'axios';
import { makeObservable, observable, runInAction } from 'mobx';

import { toast } from '@servicetitan/anvil2';
import { injectable } from '@servicetitan/react-ioc';

import { TechniciansApi } from '../../common/api/technicians.api';
import { TopicsApi, TopicType } from '../../common/api/topics.api';
import { MessageType } from '../../common/models/message-type.model';
import { toTopicViewModel } from '../../common/models/topic.model';
import { AppSettingsStore } from '../../common/stores/app-settings.store';
import { ChatStore } from '../../common/stores/chat.store';
import { MainStore } from '../../common/stores/main.store';

@injectable()
export class TopicPickerStore {
    /*
     * True when a mid-call (halftime) report exists for the appointment — drives the
     * "Mid-Call Check-In available" banner in the topic-picker sub-header.
     */
    @observable
    fieldProCoachingLive = false;

    private cancellationTokenSource?: CancelTokenSource = undefined;

    constructor(
        private readonly appSettingsStore: AppSettingsStore,
        private readonly chatStore: ChatStore,
        private readonly mainStore: MainStore,
        private readonly techniciansApi: TechniciansApi,
        private readonly topicsApi: TopicsApi
    ) {
        makeObservable(this);
        this.chatStore.resetMessaging();
        this.loadMessages();
        this.loadTopics();
    }

    loadMessages = () => {
        this.chatStore.enqueueMessages([
            {
                isMine: false,
                type: MessageType.RichText,
                text: `Hi there! I’m Atlas, an AI chatbot and I’m here to answer your questions.<br/>How can I help you with <b>${this.mainStore.selectedAppointment?.name}</b>?`,
            },
        ]);
    };

    loadTopics = async () => {
        this.cancellationTokenSource?.cancel();

        /*
         * Capture the source locally: if loadTopics is ever re-invoked mid-flight, the field is
         * overwritten and the superseded run must bail after each await instead of resuming with
         * the new token and enqueueing duplicate messages.
         */
        const source = axios.CancelToken.source();
        this.cancellationTokenSource = source;

        try {
            const { data: topicsData } = await this.topicsApi.getTopics(source.token);
            if (this.cancellationTokenSource !== source) {
                return;
            }
            const [
                {
                    data: { isEnabled: isFieldProEnabled },
                },
                isFieldProCoachingEnabled,
            ] = await Promise.all([
                this.techniciansApi.getFieldAssistProEnabled(source.token),
                this.getFieldProCoachingEnabledSafe(source.token),
            ]);
            if (this.cancellationTokenSource !== source) {
                return;
            }

            const coachingLive = await this.resolveCoachingLive(
                isFieldProCoachingEnabled,
                source.token
            );
            if (this.cancellationTokenSource !== source) {
                return;
            }
            runInAction(() => {
                this.fieldProCoachingLive = coachingLive;
            });

            this.chatStore.enqueueMessages([
                {
                    isMine: false,
                    /*
                     * Field Pro Coaching is hidden entirely when the technician isn't entitled to
                     * it (rather than shown as a locked "Upgrade" card like the other Field Pro
                     * topics), and also hidden when the chat is opened from the Cordova native
                     * wrapper since the FPC agent UI isn't supported in that context.
                     */
                    data: topicsData.topics
                        .filter(
                            topicApiModel =>
                                topicApiModel.type !== TopicType.FieldProCoaching ||
                                (isFieldProCoachingEnabled &&
                                    !this.appSettingsStore.openedFromCordova)
                        )
                        .map(topicApiModel => {
                            return toTopicViewModel(
                                topicApiModel,
                                isFieldProEnabled,
                                isFieldProCoachingEnabled
                            );
                        }),
                    type: MessageType.Topic,
                },
            ]);

            // check if equipment topic is enabled
            const isFieldProDisabled = !isFieldProEnabled;

            if (!isFieldProDisabled) {
                this.chatStore.enqueueMessages([
                    {
                        isMine: false,
                        type: MessageType.Button,
                        text: 'Replacement Part Finder',
                        data: 'ReplacementPartFinder',
                    },
                    {
                        isMine: false,
                        type: MessageType.Button,
                        text: 'Calculators & Diagnostic Tests',
                        data: 'CalculatorsDiagnosticTests',
                        date: new Date(),
                    },
                ]);
            }
        } catch (error) {
            if (axios.isCancel(error)) {
                return;
            }
            if (axios.isAxiosError(error)) {
                toast.danger({ message: error.message, title: 'Error', duration: 5000 });
                return;
            }
            throw error;
        }
    };

    /*
     * Fail-soft entitlement check: a Field-Pro-Coaching-specific outage must not take down the
     * whole topic picker (the BFF already fails closed for salespro-web errors; this covers the
     * BFF boundary itself). Cancellation still rethrows — loadTopics relies on cancel-rejection
     * to stop superseded runs from enqueueing duplicate messages.
     */
    private getFieldProCoachingEnabledSafe = async (cancelToken?: CancelToken) => {
        try {
            const { data } = await this.techniciansApi.getFieldProTopicAvailability(cancelToken);
            return data.isEnabled;
        } catch (err) {
            if (axios.isCancel(err)) {
                throw err;
            }
            return false;
        }
    };

    /*
     * Coaching is "live" when a halftime report exists for the appointment, or — on a demo tenant —
     * when one could be demo-generated for it. Without the second arm the demo button inside the
     * chat would be unreachable until a real report existed, which is the opposite of its purpose.
     * Fail-soft to not-live on any error. Never live from the Cordova native wrapper — the banner
     * deep-links into the agent chat, which (like the topic card, see the filter in loadTopics)
     * isn't supported in that context.
     */
    private resolveCoachingLive = async (
        isFieldProCoachingEnabled: boolean,
        cancelToken?: CancelToken
    ): Promise<boolean> => {
        const appointmentId = this.mainStore.selectedAppointment?.id;
        if (
            !isFieldProCoachingEnabled ||
            this.appSettingsStore.openedFromCordova ||
            !appointmentId
        ) {
            return false;
        }

        if (await this.hasMidCallReport(appointmentId, cancelToken)) {
            return true;
        }

        /*
         * Sequenced, not parallel: when a report exists this path must cost exactly what it did
         * before the demo probe existed.
         */
        return this.isDemoMcciAvailable(appointmentId, cancelToken);
    };

    // The BFF returns 204 (empty body) when no report has been generated yet.
    private hasMidCallReport = async (
        appointmentId: number,
        cancelToken?: CancelToken
    ): Promise<boolean> => {
        try {
            const { data } = await this.techniciansApi.getMidCallReport(appointmentId, cancelToken);
            return typeof data === 'object' && data !== null && Boolean(data.markdownContent);
        } catch {
            return false;
        }
    };

    private isDemoMcciAvailable = async (
        appointmentId: number,
        cancelToken?: CancelToken
    ): Promise<boolean> => {
        try {
            const { data } = await this.techniciansApi.getDemoMcciAvailability(
                appointmentId,
                cancelToken
            );
            return Boolean(data?.enabled);
        } catch {
            return false;
        }
    };
}
